import {
  DEFAULT_CHINA_LOCALE,
  DEFAULT_LOCALE,
  DEFAULT_SC_LOCALE,
  LOCALES_WITH_REGION,
} from '../constants';

/**
 * Returns the locale with region if available, otherwise returns a default locale.
 *
 * @param locale - The locale string to be processed. It can be in the format of 'en' or 'en-US'.
 * @returns The locale string with region if available, otherwise a default locale.
 */
export const getLocaleWithRegion = (locale?: string): string => {
  if (!locale) {
    return DEFAULT_LOCALE;
  }
  if (locale.includes('-')) return locale;

  const localeWithRegion = LOCALES_WITH_REGION.find((l) => l.startsWith(locale));
  return localeWithRegion ?? locale;
};

/**
 * Attaches a language prefix to a given path.
 *
 * If the language is undefined, or matches the default Sitecore or China locale,
 * the path is returned without any language prefix.
 *
 * @param language - The language code to attach to the path. Can be undefined.
 * @param path - The path to which the language code should be attached. Defaults to an empty string.
 * @returns The path with the language prefix attached, if applicable.
 */
export const attachLanguageToPath = (language: string | undefined, path: string = ''): string => {
  if (!language || language === DEFAULT_SC_LOCALE || language === DEFAULT_CHINA_LOCALE) {
    return `/${path.replace(/^\//, '')}`;
  }

  const localePrefix = `${language}/`;
  const cleanedPath = path.replace(/^\//, '');

  if (cleanedPath.toLowerCase().startsWith(localePrefix.toLowerCase())) {
    const localePattern = new RegExp(localePrefix, 'i');
    const updatedPath: string = cleanedPath.replace(localePattern, localePrefix);

    return `/${updatedPath}`;
  }

  return `/${localePrefix}${cleanedPath}`;
};
